<template>
  <div id="otj-inbox" class="content-with-spacing">
    <div class="heading-container">
      <h1><i class="fas fa-clock" aria-hidden="true"></i>
        O.T.J. Inbox
      </h1>
    </div>
    <div class="panel panel-default">
      <div class="panel-body">
        <div class="button-container">
          <button class="btn btn-primary otj-inbox-btn" @click="getLearners"><i class="fas fa-sync" aria-hidden="true"></i> Reload Table</button>
        </div>
        <k-table class="otj-inbox-table"
          v-if="learners?.length > 0"
          :headers="kableHeaders"
          :rows="kableRows"
          :hideable="false"
          :panel="false"
          :max="10"
          @clicked="handleTableAction"
        ></k-table>
        <empty-placeholder
          v-else
          title="No learners found"
          info="No learners with pending OTJs were found."
        ></empty-placeholder>
      </div>
    </div>
    <pending-otj-modal
      v-if="otjsToReview?.length > 0 && programmeId"
      :show="showPendingOtjModal"
      :otjs="otjsToReview"
      :programmeId="programmeId"
      :learnerId="learnerId"
      :learnerName="learnerName"
      :programmeMemberId="programmeMemberId"
      :programmeName="programmeName"
      @update="getLearners"
      @close="showPendingOtjModal = false"
    />
  </div>
</template>

<style scoped>
.panel-body {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.otj-inbox-btn {
  margin-bottom: 10px;
}
</style>

<style>
#otj-inbox .otj-inbox-table td.k-table-key-student_id,
#otj-inbox .otj-inbox-table td.k-table-key-name {
  text-align: left;
}

#otj-inbox .otj-inbox-table td {
  text-align: center;
  align-content: center;
}

.otj-inbox-table .k-table-header-content {
  text-align: center;
}

.otj-inbox-table .k-table-header-text:only-child {
  flex: 1;
}
</style>

<script>
import KTable from '@base-components/tables/display/k-table.vue';
import { formatId } from '@utils/format-user-id';
import TimeMixin from '@mixins/time-mixins';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import useGlobalStore from '@stores/global';
import PendingOtjModal from './pending-otj-modal.vue';

export default {
  components: {
    KTable,
    PendingOtjModal,
    EmptyPlaceholder,
  },

  mixins: [TimeMixin],

  data() {
    return {
      store: useGlobalStore(),
      learners: [],
      learnersReady: false,
      showPendingOtjModal: false,
      programmeId: undefined,
      learnerId: undefined,
      programmeMemberId: undefined,
      programmeName: '',
      learnerName: '',
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getLearners();
  },

  computed: {
    coachUserId() {
      return this.store.userId;
    },

    kableHeaders() {
      return {
        student_id: {
          name: 'Student ID',
          type: 'string',
          filterable: true,
        },
        name: {
          name: 'Name',
          type: 'url',
          filterable: true,
          sortable: true,
        },
        total_hours: {
          name: 'Total Hours',
          type: 'string',
          sortable: true,
          displayAs: timeSpent => this.formatTimeSpent(timeSpent),
        },
        last_submission: {
          name: 'Last Submission',
          type: 'timestamp',
          sortable: true,
        },
        programme: {
          name: 'Programme',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        otjs_waiting: {
          name: 'OTJs waiting review',
          type: 'string',
        },
        review: {
          name: 'Review',
          type: 'button',
          options: {
            preset: 'view',
            title: 'Review',
            ariaLabel: row => `Review ${row.otjs_waiting} OTJs from ${row.name.text}`,
          },
        },
      };
    },

    kableRows() {
      return this.learners.map(learner => ({
        student_id: formatId(learner.student_id),
        name: {
          text: learner.student_name,
          path: {
            name: 'dashboard_students',
            params: {
              studentId: learner.student_id,
            },
          },
        },
        total_hours: learner.total_time_spent,
        last_submission: learner.latest_otj_submission,
        programme: learner.programme_name,
        otjs_waiting: learner.pending_otj_count,
      }));
    },

    otjsToReview() {
      if (!this.learners) {
        return [];
      }
      const selectedLearner = this.learners.find(l => l.student_id === this.learnerId && l.programme_id === this.programmeId);
      if (!selectedLearner) {
        return [];
      }
      return selectedLearner.pending_otj_entries;
    },
  },

  methods: {
    getLearners() {
      this.learnersReady = false;
      this.$Loading.start();
      this.$logger.info('Fetching learners and pending OTJs', { coachUserId: this.coachUserId });
      this.$http.get(`/api/curriculum/admin/programmes/otj-entries/${this.coachUserId}`)
        .then(response => {
          this.$logger.info('Successfully fetched learners and pending OTJs', { coachUserId: this.coachUserId });
          this.learners = response.data;
          this.$sidebar.otjsToReview = response.data.reduce((total, learner) => total + learner.pending_otj_count, 0);
        })
        .catch(error => {
          this.$logger.error('Error fetching learners and pending OTJs', { coachUserId: this.coachUserId }, error);
          this.$ktoast.showError(error, true);
        }).then(() => {
          this.learnersReady = true;
          this.$Loading.finish();
        });
    },

    handleTableAction(key, index) {
      const learner = this.learners[index];
      this.programmeId = learner.programme_id;
      this.learnerId = learner.student_id;
      this.learnerName = learner.student_name;
      this.programmeName = learner.programme_name;
      this.programmeMemberId = learner.programme_member_id;
      this.$nextTick(() => {
        this.showPendingOtjModal = true;
      });
    },
  },
};
</script>
